import { msg } from "@lingui/core/macro";
import { Show } from "solid-js";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useI18n } from "@/providers/i18n-provider";
import type { DirectoryUser } from "./user-detail-view";

function row(label: string, value: () => unknown) {
  return (
    <div class="flex items-start justify-between gap-4 py-2.5 border-b border-border last:border-0">
      <span class="text-sm text-muted-foreground">{label}</span>
      <span class="text-sm font-medium text-right break-all">{String(value() ?? "—")}</span>
    </div>
  );
}

function fmtDate(iso: string | null | undefined) {
  return iso ? new Date(iso).toLocaleString() : "—";
}

/** F2-C — read-only profile fields of a user (perfil tab). */
export function ProfileTab(props: { user: DirectoryUser }) {
  const { t } = useI18n();

  return (
    <Card class="mt-4">
      <CardContent class="p-5">
        {row(t(msg`User ID`), () => props.user.id)}
        {row(t(msg`Name`), () => props.user.name)}
        {row(t(msg`Email`), () => props.user.email)}
        <div class="flex items-center justify-between gap-4 py-2.5 border-b border-border">
          <span class="text-sm text-muted-foreground">{t(msg`Email verified`)}</span>
          <Badge variant={props.user.emailVerified ? "secondary" : "outline"}>
            {props.user.emailVerified ? t(msg`Verified`) : t(msg`Unverified`)}
          </Badge>
        </div>
        {row(t(msg`Platform role`), () => props.user.role)}
        <Show when={props.user.nationalId}>
          {(cedula) => row(t(msg`National ID (cédula)`), () => cedula())}
        </Show>
        <div class="flex items-center justify-between gap-4 py-2.5 border-b border-border">
          <span class="text-sm text-muted-foreground">{t(msg`Status`)}</span>
          <Badge variant={props.user.banned ? "destructive" : "secondary"}>
            {props.user.banned ? t(msg`Suspended`) : t(msg`Active`)}
          </Badge>
        </div>
        <Show when={props.user.banned && props.user.banReason}>
          {row(t(msg`Suspension reason`), () => props.user.banReason)}
        </Show>
        {row(t(msg`Created`), () => fmtDate(props.user.createdAt))}
        {row(t(msg`Updated`), () => fmtDate(props.user.updatedAt))}
      </CardContent>
    </Card>
  );
}
